import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/ui/Button'
import { Modal } from 'components/ui/Modal'
import { Badge } from 'components/ui/Badge'
import { 
  userManagementApi, 
  type UserWithRoles,
  type User
} from 'lib/api'
import { useToast } from 'contexts/ToastContext'
import { 
  User as UserIcon, 
  Mail, 
  Phone, 
  Building2, 
  Calendar,
  Shield,
  CheckCircle2,
  XCircle,
  Loader2
} from 'lucide-react'

interface UserProfileModalProps {
  isOpen: boolean
  onClose: () => void
  user: UserWithRoles | null
  onToggleActive?: (user: UserWithRoles, newValue: boolean) => void
  updatingStatusUserId?: number | null
}

export function UserProfileModal({ isOpen, onClose, user, onToggleActive, updatingStatusUserId }: UserProfileModalProps) {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const [userDetails, setUserDetails] = useState<User | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isOpen && user) {
      fetchUserDetails()
    } else {
      setUserDetails(null)
    }
  }, [isOpen, user?.id])

  const fetchUserDetails = async () => {
    if (!user) return

    try {
      setLoading(true)
      const response = await userManagementApi.getUser(user.id)
      setUserDetails(response.data)
    } catch (error) {
      console.error('Error fetching user details:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('users.messages.loadUserError', 'Failed to load user details'),
        duration: 5000
      })
    } finally {
      setLoading(false)
    }
  }

  if (!user) return null

  // Merge userDetails with user prop, prioritizing user prop for status (always up-to-date)
  const displayUser: UserWithRoles & Partial<User> = userDetails
    ? {
        ...userDetails,
        roles: user.roles,
        // Always use the latest status from user prop (updated optimistically in parent)
        is_active: user.is_active,
        is_staff: user.is_staff,
        is_superuser: user.is_superuser,
      }
    : user

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('users.profile', 'User Profile')}
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* User Header */}
          <div className="flex items-start space-x-4 pb-6 border-b">
            <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              {displayUser.avatar_url ? (
                <img 
                  src={displayUser.avatar_url} 
                  alt={displayUser.full_name || displayUser.email}
                  className="h-20 w-20 rounded-full object-cover"
                />
              ) : (
                <UserIcon className="h-10 w-10 text-primary" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold text-foreground truncate">
                {displayUser.full_name || displayUser.email}
              </h2>
              <p className="text-muted-foreground flex items-center gap-2 mt-1">
                <Mail className="h-4 w-4" />
                {displayUser.email}
              </p>
              <div className="flex items-center gap-2 mt-3">
                {onToggleActive && user ? (
                  <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 relative transition-all duration-200`}>
                    {!displayUser.is_active && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onToggleActive(user, true)}
                        disabled={updatingStatusUserId === user.id}
                        title={t('users.activateUser', 'Activate user')}
                        className="h-6 w-6 p-0 bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50 rounded-full transition-all duration-200"
                        aria-label={t('users.activateUser', 'Activate user')}
                      >
                        {updatingStatusUserId === user.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <CheckCircle2 className="h-3 w-3" />
                        )}
                      </Button>
                    )}
                    <span
                      className={`h-2.5 w-2.5 rounded-full transition-colors duration-200 ${
                        displayUser.is_active 
                          ? 'bg-green-500 dark:bg-green-400' 
                          : 'bg-red-500 dark:bg-red-400'
                      }`}
                    ></span>
                    <span className={`text-xs transition-colors duration-200`}>
                      {displayUser.is_active ? t('common.active') : t('common.inactive')}
                    </span>
                    {displayUser.is_active && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onToggleActive(user, false)}
                        disabled={updatingStatusUserId === user.id}
                        title={t('users.deactivateUser', 'Deactivate user')}
                        className="h-6 w-6 p-0 bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 rounded-full transition-all duration-200"
                        aria-label={t('users.deactivateUser', 'Deactivate user')}
                      >
                        {updatingStatusUserId === user.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <XCircle className="h-3 w-3" />
                        )}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 ${
                    displayUser.is_active 
                      ? 'bg-green-100 border-green-200 dark:bg-green-900/30 dark:border-green-800' 
                      : 'bg-red-100 border-red-200 dark:bg-red-900/30 dark:border-red-800'
                  }`}>
                    <span
                      className={`h-2 w-2 rounded-full ${
                        displayUser.is_active 
                          ? 'bg-green-500 dark:bg-green-400' 
                          : 'bg-red-500 dark:bg-red-400'
                      }`}
                    ></span>
                    <span className="text-xs font-medium">
                      {displayUser.is_active ? t('common.active') : t('common.inactive')}
                    </span>
                  </div>
                )}
                {displayUser.is_staff && (
                  <Badge variant="secondary" className="text-xs">
                    <Shield className="h-3 w-3 mr-1" />
                    {t('users.staff', 'Staff')}
                  </Badge>
                )}
                {displayUser.is_superuser && (
                  <Badge variant="secondary" className="text-xs">
                    <Shield className="h-3 w-3 mr-1" />
                    {t('users.superuser', 'Superuser')}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* User Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">
                {t('users.basicInformation', 'Basic Information')}
              </h3>
              
              <div className="space-y-3">
                {displayUser.username && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <UserIcon className="h-4 w-4" />
                      {t('users.username', 'Username')}
                    </label>
                    <p className="mt-1 text-foreground">{displayUser.username}</p>
                  </div>
                )}

                {displayUser.first_name && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('users.firstName', 'First Name')}
                    </label>
                    <p className="mt-1 text-foreground">{displayUser.first_name}</p>
                  </div>
                )}

                {displayUser.last_name && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('users.lastName', 'Last Name')}
                    </label>
                    <p className="mt-1 text-foreground">{displayUser.last_name}</p>
                  </div>
                )}

                {displayUser.company && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      {t('users.company', 'Company')}
                    </label>
                    <p className="mt-1 text-foreground">{displayUser.company}</p>
                  </div>
                )}

                {displayUser.phone && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {t('users.phone', 'Phone')}
                    </label>
                    <p className="mt-1 text-foreground">{displayUser.phone}</p>
                  </div>
                )}

                {displayUser.date_joined && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {t('users.dateJoined', 'Date Joined')}
                    </label>
                    <p className="mt-1 text-foreground">
                      {new Date(displayUser.date_joined).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Roles & Permissions */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">
                {t('users.rolesAndPermissions', 'Roles & Permissions')}
              </h3>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t('common.roles', 'Roles')}
                  </label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {user.roles && user.roles.length > 0 ? (
                      user.roles.map((userRole) => (
                        <Badge 
                          key={userRole.id}
                          style={{ backgroundColor: userRole.role_color, color: 'white' }}
                          className="text-xs"
                        >
                          {userRole.role_name}
                        </Badge>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {t('users.noRoles', 'No roles assigned')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
        <Button 
          variant="outline" 
          onClick={onClose}
        >
          {t('common.close', 'Close')}
        </Button>
      </div>
    </Modal>
  )
}

